import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search, TrendingUp, Users, Briefcase, ArrowRight, Loader2,
  LogOut, User as UserIcon, Sparkles,
  Network, BarChart3, Building2, CheckCircle2, Star
} from 'lucide-react';
import { toast } from 'sonner';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { getOrgTransitions, searchOrganizations } from '../api/insights';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import ThemeToggle from './ThemeToggle';
import { trackCoreFeatureUsed, incrementActivationCounter, trackCompanySearch } from '../analytics/mixpanel';
import { liteClient } from 'algoliasearch/lite';

const algoliaClient = liteClient(
  process.env.REACT_APP_ALGOLIA_APP_ID || 'PLACEHOLDER_APP_ID',
  process.env.REACT_APP_ALGOLIA_SEARCH_KEY || 'PLACEHOLDER_SEARCH_KEY'
);
const ALGOLIA_INDEX = process.env.REACT_APP_ALGOLIA_INDEX_NAME || 'organizations';

const SUGGESTION_DEBOUNCE_MS = 500;
const MIN_QUERY_LENGTH = 1;
const DASHBOARD_STATE_STORAGE_KEY = 'insightsDashboardState';
const PROFILE_SETUP_REQUIRED_KEY = 'insights_profile_setup_required';
const DESTINATION_MIN_COUNT = 5;
const DEFAULT_TENURE_YEARS = 5;
const EXPANDED_TENURE_YEARS = 15;

const getDashboardStateStorageKey = (u) =>
  `${DASHBOARD_STATE_STORAGE_KEY}:${String(u?.id || u?.email || 'anonymous')}`;
const getProfileSetupRequiredKey = (u) =>
  `${PROFILE_SETUP_REQUIRED_KEY}:${String(u?.id || u?.email || 'anonymous')}`;

/* ── Animated node for the hero visualization ── */
const Node = ({ x, y, label, delay = 0, color = '#F59E0B', size = 9, isDark = true }) => (
  <motion.g
    initial={{ opacity: 0, scale: 0 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ delay, duration: 0.5, ease: [0.34, 1.56, 0.64, 1] }}
  >
    <circle cx={x} cy={y} r={size} fill={color} fillOpacity={0.15} stroke={color} strokeWidth={1.5} />
    <circle cx={x} cy={y} r={size * 0.5} fill={color} fillOpacity={0.9} />
    {label && (
      <text x={x} y={y + size + 12} textAnchor="middle" fill={isDark ? "rgba(255,255,255,0.6)" : "#475569"} fontSize="10" fontWeight="600" fontFamily="Manrope, sans-serif">
        {label}
      </text>
    )}
  </motion.g>
);

const Edge = ({ x1, y1, x2, y2, delay = 0, color = 'rgba(245,158,11,0.3)' }) => (
  <motion.path
    d={`M ${x1} ${y1} L ${x2} ${y2}`}
    stroke={color} strokeWidth={1.5} strokeDasharray="5 5" fill="none"
    initial={{ pathLength: 0, opacity: 0 }}
    animate={{ pathLength: 1, opacity: 1 }}
    transition={{ delay, duration: 0.9, ease: 'easeOut' }}
  />
);

const NetworkViz = ({ isDark = true }) => {
  const accentColor = isDark ? '#F59E0B' : '#2563eb';
  const nodes = [
    { x: 100, y: 110, label: 'Google', color: accentColor, size: 13, delay: 0.1 },
    { x: 260, y: 55, label: 'Stripe', color: '#10B981', size: 10, delay: 0.3 },
    { x: 330, y: 175, label: 'Notion', color: '#3B82F6', size: 9, delay: 0.5 },
    { x: 185, y: 250, label: 'Figma', color: '#8B5CF6', size: 10, delay: 0.4 },
    { x: 70, y: 310, label: 'Vercel', color: '#EC4899', size: 8, delay: 0.6 },
    { x: 390, y: 90, label: 'Linear', color: accentColor, size: 8, delay: 0.7 },
    { x: 360, y: 300, label: 'Runway', color: '#10B981', size: 7, delay: 0.8 },
    { x: 145, y: 380, label: 'Loom', color: '#3B82F6', size: 7, delay: 0.9 },
    { x: 295, y: 390, label: 'Atlassian', color: accentColor, size: 6, delay: 1.0 },
  ];
  const edges = [
    [0, 1], [0, 2], [0, 3], [1, 2], [1, 5], [2, 3], [2, 6], [3, 4], [3, 7], [4, 7], [6, 8], [7, 8]
  ];
  return (
    <svg viewBox="0 0 460 450" width="100%" height="100%" style={{ filter: `drop-shadow(0 0 24px ${isDark ? 'rgba(245,158,11,0.12)' : 'rgba(37,99,235,0.1)'})` }}>
      <defs>
        <radialGradient id="vizglow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor={accentColor} stopOpacity="0.06" />
          <stop offset="100%" stopColor={accentColor} stopOpacity="0" />
        </radialGradient>
      </defs>
      <ellipse cx="230" cy="225" rx="200" ry="210" fill="url(#vizglow)" />
      {edges.map(([i, j], idx) => (
        <Edge key={idx} x1={nodes[i].x} y1={nodes[i].y} x2={nodes[j].x} y2={nodes[j].y} delay={0.15 + idx * 0.07} color={isDark ? 'rgba(245,158,11,0.25)' : 'rgba(37,99,235,0.2)'} />
      ))}
      {nodes.map((n, i) => <Node key={i} {...n} isDark={isDark} />)}
    </svg>
  );
};

const LandingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { referralId } = useParams();
  const { user, logout, openLogin } = useAuth();
  const { isDark } = useTheme();
  const companyInputRef = useRef(null);

  const theme = {
    bg: isDark ? '#0d1019' : '#f8fafc',
    textPrimary: isDark ? '#ffffff' : '#0f172a',
    textSecondary: isDark ? 'rgba(255,255,255,0.5)' : '#475569',
    textMuted: isDark ? 'rgba(255,255,255,0.35)' : '#64748b',
    textDim: isDark ? 'rgba(255,255,255,0.2)' : '#94a3b8',
    border: isDark ? 'rgba(255,255,255,0.06)' : '#e2e8f0',
    cardBg: isDark ? 'rgba(255,255,255,0.04)' : '#ffffff',
    cardBorder: isDark ? 'rgba(255,255,255,0.1)' : '#f1f5f9',
    accent: '#F59E0B',
    accentSecondary: isDark ? '#10B981' : '#059669',
    panelBg: isDark ? 'rgba(13,16,25,0.85)' : 'rgba(255,255,255,0.85)',
    inputBg: isDark ? 'rgba(255,255,255,0.06)' : '#ffffff',
    inputBorder: isDark ? 'rgba(255,255,255,0.1)' : '#e2e8f0',
    heroGlow: isDark ? 'rgba(245,158,11,0.06)' : 'rgba(37,99,235,0.04)',
  };

  const [companyName, setCompanyName] = useState('');
  const [searching, setSearching] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [suggestionsError, setSuggestionsError] = useState(null);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const suggestionRef = useRef(null);
  const autoLoginTriggeredRef = useRef(false);
  const currentYear = new Date().getFullYear();
  const defaultStartYear = String(currentYear - DEFAULT_TENURE_YEARS);
  const defaultEndYear = String(currentYear);
  const expandedStartYear = String(currentYear - EXPANDED_TENURE_YEARS);

  const countDestinationCompanies = async (orgId, startYear, endYear) => {
    const startDate = `${startYear}-01-01`;
    const endDate = `${endYear}-12-31`;
    const data = await getOrgTransitions(orgId, { startDate, endDate, hops: 1 });
    const destinationIds = new Set();
    Object.entries(data || {}).forEach(([key, value]) => {
      if (!Array.isArray(value)) return;
      if (Number.isNaN(Number(key))) return;
      value.forEach((item) => {
        if (item?.organization_id != null) {
          destinationIds.add(String(item.organization_id));
        }
      });
    });
    return destinationIds.size;
  };

  useEffect(() => {
    if (!(location.pathname || '').startsWith('/ref/') || !referralId) return;
    localStorage.setItem('insights_referral_source', `${window.location.origin}/ref/${referralId}`);
  }, [location.pathname, referralId]);

  useEffect(() => {
    const postLoginRedirect = location?.state?.postLoginRedirect;
    if (user) return;
    if (!postLoginRedirect || typeof postLoginRedirect !== 'string') return;
    if (autoLoginTriggeredRef.current) return;
    autoLoginTriggeredRef.current = true;
    openLogin(() => {
      navigate(postLoginRedirect, { replace: true });
    });
  }, [location, user, openLogin, navigate]);

  useEffect(() => {
    const q = (companyName || '').trim();
    if (q.length < MIN_QUERY_LENGTH) { setSuggestions([]); setSuggestionsError(null); setLoadingSuggestions(false); return; }
    const t = setTimeout(async () => {
      setLoadingSuggestions(true);
      try {
        const { results } = await algoliaClient.search({
          requests: [
            {
              indexName: ALGOLIA_INDEX,
              query: q,
              hitsPerPage: 20,
            }
          ]
        });
        const hits = results[0]?.hits || [];
        setSuggestions(hits.map(hit => ({
          id: hit.id,
          name: hit.name,
          alumni_count: hit.alumni_count,
          related_count: 0
        })));
        setSuggestionsError(null);
      } catch (err) {
        setSuggestions([]);
        setSuggestionsError(err.message || 'Could not load suggestions from Algolia');
      } finally {
        setLoadingSuggestions(false);
      }
    }, SUGGESTION_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [companyName]);

  useEffect(() => {
    const fn = (e) => { if (suggestionRef.current && !suggestionRef.current.contains(e.target)) setSuggestionsOpen(false); };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, []);

  const handleCompanyChange = (e) => {
    const v = e.target.value;
    setCompanyName(v); setSelectedOrg(null);
    setSuggestionsOpen(v.trim().length >= MIN_QUERY_LENGTH);
  };

  const handleSelectOrg = (org) => {
    setSelectedOrg(org); setCompanyName(org.name);
    setSuggestionsOpen(false); setSuggestions([]); setSuggestionsError(null);
  };

  const performSearch = async (forceResolved = null, authResult = null) => {
    setSearching(true);
    try {
      const eu = authResult?.user || user;
      const sk = getDashboardStateStorageKey(eu);
      const requiresSetup = !!authResult?.isNewUser || (() => { try { return localStorage.getItem(getProfileSetupRequiredKey(eu)) === '1'; } catch { return false; } })();
      let resolved = forceResolved || selectedOrg;
      if (!resolved) {
        trackCompanySearch(companyName.trim(), false, {
          source: 'landing_page',
          start_year: defaultStartYear,
          end_year: defaultEndYear,
        });
        const orgs = await searchOrganizations(companyName);
        if (!orgs || orgs.length === 0) {
          // Org not found → go to dashboard (shows the 'no data yet' banner)
          const state = { companyName: companyName.trim(), startYear: defaultStartYear, endYear: defaultEndYear, orgNotFound: true };
          try { sessionStorage.setItem(sk, JSON.stringify(state)); } catch { }
          navigate(requiresSetup ? '/profile-setup' : '/dashboard', { state }); return;
        }
        resolved = orgs[0];
      }
      trackCompanySearch(resolved.name, true, {
        source: 'landing_page',
        org_id: resolved.id,
        start_year: defaultStartYear,
        end_year: defaultEndYear,
      });

      let selectedStartYear = defaultStartYear;
      let destinationCompaniesCount = DESTINATION_MIN_COUNT;
      let destinationCountResolved = false;

      const alumCount = Number(resolved?.alumni_count);
      if (Number.isFinite(alumCount) && alumCount >= 30) {
        // Shortcut: If alumni count is already large, we are guaranteed to have enough destinations.
        // Skip the heavy transitions query entirely to make this transition instant!
        destinationCompaniesCount = alumCount;
        destinationCountResolved = true;
        console.log(`[LandingSearch] ⚡ Shortcut: Large alumni count (${alumCount}) -> skipping transitions count check`);
      } else if (Number.isFinite(alumCount) && alumCount < DESTINATION_MIN_COUNT) {
        // Shortcut: If total lifetime alumni count is less than 5, it can never have >= 5 destinations in any timeframe.
        destinationCompaniesCount = alumCount;
        destinationCountResolved = true;
        selectedStartYear = expandedStartYear;
        console.log(`[LandingSearch] ⚡ Shortcut: Small alumni count (${alumCount}) -> fallback to expanded start year`);
      } else {
        // Fallback or middle range: Perform a highly optimized 1-hop check
        try {
          destinationCompaniesCount = await countDestinationCompanies(resolved.id, defaultStartYear, defaultEndYear);
          destinationCountResolved = true;
          if (destinationCompaniesCount < DESTINATION_MIN_COUNT) {
            selectedStartYear = expandedStartYear;
            destinationCompaniesCount = await countDestinationCompanies(resolved.id, expandedStartYear, defaultEndYear);
          }
        } catch (countErr) {
          console.error('Failed to evaluate destination company count', countErr);
        }
      }

      const hasInsufficientDestinationCompanies = destinationCountResolved && destinationCompaniesCount < DESTINATION_MIN_COUNT;
      const state = {
        orgId: resolved.id,
        companyName: resolved.name,
        startYear: selectedStartYear,
        endYear: defaultEndYear,
        destinationCompaniesCount,
        insufficientDestinations: hasInsufficientDestinationCompanies,
      };
      try { sessionStorage.setItem(sk, JSON.stringify(state)); } catch { }
      trackCoreFeatureUsed('company_search', {
        company_name: resolved.name,
        org_id: resolved.id,
        start_year: selectedStartYear,
        end_year: defaultEndYear,
        destination_companies_count: destinationCompaniesCount,
      });
      incrementActivationCounter('company_searches');

      if (hasInsufficientDestinationCompanies) {
        toast.info(`Only ${destinationCompaniesCount} destination companies found for ${resolved.name}. Showing alternatives instead.`);
        navigate(requiresSetup ? '/profile-setup' : '/dashboard', { state });
        return;
      }

      toast.success(`Loading insights for ${resolved.name}`);
      // Go to insights screen first for high-level aggregate answers
      navigate(requiresSetup ? '/profile-setup' : '/insights', { state });
    } catch (err) { toast.error(err.message || 'Failed to search organizations'); }
    finally { setSearching(false); }
  };

  const handleSearch = async (e) => {
    if (e) e.preventDefault();
    if (!companyName.trim()) { toast.error('Please enter a company name'); return; }

    // Start the rating timer only after this interaction
    sessionStorage.setItem('insights_tracking_started', 'true');

    if (!user) { openLogin(r => performSearch(null, r)); return; }
    await performSearch();
  };

  const handleGetStarted = () => { window.scrollTo({ top: 0, behavior: 'smooth' }); setTimeout(() => companyInputRef.current?.focus(), 350); };


  const FEATURES = [
    { icon: TrendingUp, label: 'Career Transitions', color: '#F59E0B', desc: 'See exactly where your alumni network is moving — by role, sector, and seniority.', },
    { icon: Network, label: 'Network Intelligence', color: '#10B981', desc: 'Find colleagues who share your background inside every destination company.', },
    { icon: BarChart3, label: 'Opportunity Signals', color: '#3B82F6', desc: 'Identify which companies are repeatedly pulling from your network right now.', },
  ];

  const STEPS = [
    { n: '01', icon: Building2, title: 'Enter your company & tenure', desc: 'Tell us where you worked and for how long.' },
    { n: '02', icon: Sparkles, title: 'We map the transitions', desc: 'Our engine traces thousands of real career moves from that company.' },
    { n: '03', icon: TrendingUp, title: 'Get ranked opportunities', desc: 'See every destination company ranked by network reach & recent hires.' },
  ];

  const PROOF = [
    { text: 'Applied only to the right companies and got interview calls within days.', name: 'Praneeth K', role: 'Product Manager' },
    { text: 'Quickly spotted companies where I had real referral opportunities.', name: 'Subash', role: 'Software Engineer' },
    { text: 'Cut my job search time by more than half.', name: 'Madhan', role: 'Software Engineer' },
  ];

  const fadeUp = (delay = 0) => ({
    initial: { opacity: 0, y: 28 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: '-60px' },
    transition: { duration: 0.65, ease: [0.22, 1, 0.36, 1], delay },
  });

  return (
    <div className={isDark ? 'dark-app' : 'light-app'} style={{ fontFamily: "'Manrope', sans-serif", background: theme.bg, color: theme.textPrimary, overflowX: 'hidden', minHeight: '100vh' }}>

      {/* ── NAV ── */}
      <header className="nav-container" style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 50,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        background: theme.panelBg, backdropFilter: 'blur(16px)',
        borderBottom: `1px solid ${theme.border}`,
      }}>
        <div onClick={() => navigate('/')} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
          <div style={{ width: 34, height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fff', borderRadius: '8px', padding: '4px', border: isDark ? 'none' : '1px solid #e2e8f0' }}>
            <img src="/assets/logo.png" alt="fairhire logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
          </div>
          <span style={{ fontSize: 18, fontWeight: 800, color: theme.textPrimary, fontFamily: "'Playfair Display', serif" }}>fairhire</span>
        </div>
        <div style={{ position: 'relative' }}>
          {user ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <ThemeToggle />
              <div style={{ position: 'relative' }}>
                <button
                  data-testid="profile-icon"
                  onClick={() => setIsProfileMenuOpen(v => !v)}
                  style={{
                    width: 34, height: 34, borderRadius: '50%',
                    background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(37,99,235,0.1)',
                    border: isDark ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(37,99,235,0.2)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer'
                  }}
                ><UserIcon size={16} color={isDark ? 'rgba(255,255,255,0.8)' : '#2563eb'} /></button>
                <AnimatePresence>
                  {isProfileMenuOpen && (
                    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }}
                      style={{
                        position: 'absolute', right: 0, top: 'calc(100% + 8px)', width: 200,
                        background: isDark ? 'rgba(13,16,25,0.98)' : '#ffffff',
                        border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid #dbe7fb',
                        borderRadius: 14, padding: 8,
                        boxShadow: isDark ? '0 16px 48px rgba(0,0,0,0.6)' : '0 12px 36px rgba(37,99,235,0.18)'
                      }}>
                      <div style={{ padding: '8px 12px', borderBottom: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e2e8f0', marginBottom: 4 }}>
                        <p style={{ fontSize: 11, color: theme.textMuted, marginBottom: 2 }}>Signed in as</p>
                        <p style={{ fontSize: 13, fontWeight: 600, color: theme.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.name}</p>
                      </div>
                      {[
                        { label: 'Profile', icon: <UserIcon size={14} />, onClick: () => { setIsProfileMenuOpen(false); navigate('/profile'); }, color: theme.textSecondary },
                        { label: 'Logout', icon: <LogOut size={14} />, onClick: () => { logout(); setIsProfileMenuOpen(false); navigate('/'); toast.success('Logged out'); }, color: '#f87171' },
                      ].map(item => (
                        <button key={item.label} onClick={item.onClick} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderRadius: 8, background: 'none', border: 'none', color: item.color, fontSize: 13, cursor: 'pointer' }}
                          onMouseEnter={e => e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(37,99,235,0.08)'}
                          onMouseLeave={e => e.currentTarget.style.background = 'none'}
                        >{item.icon}{item.label}</button>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <ThemeToggle />
              <button
                data-testid="login-button"
                onClick={() => openLogin()}
                style={{ padding: '8px 18px', borderRadius: 100, background: isDark ? 'rgba(255,255,255,0.08)' : '#f8fafc', border: `1px solid ${theme.border}`, color: theme.textSecondary, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                onMouseEnter={e => { e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.14)' : '#f1f5f9'; e.currentTarget.style.color = theme.textPrimary; }}
                onMouseLeave={e => { e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.08)' : '#f8fafc'; e.currentTarget.style.color = theme.textSecondary; }}
              >Sign in</button>
            </div>
          )}
        </div>
      </header>

      {/* ── HERO ── */}
      <section className="hero-section" style={{ maxWidth: 1360, margin: '0 auto', position: 'relative' }}>
        {/* subtle noise background */}
        <div style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 0, opacity: isDark ? 0.03 : 0.015, backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 200 200\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'n\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23n)\'/%3E%3C/svg%3E")', backgroundSize: '200px' }} />

        {/* Left: headline + form */}
        <motion.div
          initial="hidden"
          animate="visible"
          variants={{ visible: { transition: { staggerChildren: 0.1 } } }}
          className="hero-text-container"
          style={{ position: 'relative', zIndex: 2 }}
        >
          {/* pill */}
          <motion.div variants={{ hidden: { opacity: 0, y: 16 }, visible: { opacity: 1, y: 0, transition: { duration: 0.6 } } }}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 12px', borderRadius: 100, background: isDark ? 'rgba(245,158,11,0.12)' : 'rgba(37,99,235,0.08)', border: isDark ? '1px solid rgba(245,158,11,0.3)' : '1px solid rgba(37,99,235,0.2)', marginBottom: 22 }}>
            <Sparkles size={12} color={isDark ? theme.accent : '#2563eb'} />
            <span style={{ fontSize: 11, fontWeight: 700, color: isDark ? theme.accent : '#2563eb', letterSpacing: '0.12em', textTransform: 'uppercase' }}>Alumni Network Intelligence</span>
          </motion.div>

          {/* Headline */}
          <motion.h1
            variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0, transition: { duration: 0.7 } } }}
            style={{ fontFamily: "'DM Serif Display 400', DM Serif Display 400", fontSize: 'clamp(32px, 3.8vw, 56px)', fontWeight: 800, lineHeight: 1.12, marginBottom: 16, color: theme.textPrimary }}
          >
            Know where your <br />
            network is headed<br />
            <span style={{ color: isDark ? theme.accent : '#2563eb', textShadow: isDark ? '0 0 80px rgba(245,158,11,0.4)' : 'none' }}>before you apply.</span>
          </motion.h1>

          <motion.p
            variants={{ hidden: { opacity: 0, y: 16 }, visible: { opacity: 1, y: 0, transition: { duration: 0.6 } } }}
            style={{ fontSize: 14, lineHeight: 1.65, color: theme.textSecondary, marginBottom: 28, maxWidth: 430 }}
          >
            Track career transitions across your alumni network. Surface hidden connections, spot hiring patterns, and approach every company with an insider edge.
          </motion.p>

          {/* Search Form */}
          <motion.form
            variants={{ hidden: { opacity: 0, y: 16 }, visible: { opacity: 1, y: 0, transition: { duration: 0.6 } } }}
            onSubmit={handleSearch}
            data-testid="alumni-search-form"
            style={{
              background: isDark ? 'rgba(255,255,255,0.04)' : '#ffffff',
              border: `1px solid ${theme.border}`,
              borderRadius: 12,
              padding: 18,
              backdropFilter: 'blur(12px)',
              maxWidth: 540,
              width: '100%',
              boxShadow: isDark ? 'none' : '0 12px 36px rgba(0,0,0,0.06)'
            }}
          >
            {/* Company */}
            <div ref={suggestionRef} style={{ marginBottom: 16, position: 'relative' }}>
              <label style={{ fontSize: 11, fontWeight: 700, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: '0.1em', display: 'block', marginBottom: 8 }}>Company</label>
              <div style={{ position: 'relative' }}>
                <Search size={15} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: theme.textDim, pointerEvents: 'none' }} />
                <input
                  ref={companyInputRef}
                  id="company-name"
                  type="text"
                  placeholder="Enter your current or previous company(e.g. Google, Flipkart..)"
                  value={companyName}
                  onChange={handleCompanyChange}
                  onFocus={(e) => {
                    const focusColor = isDark ? 'rgba(245,158,11,0.6)' : 'rgba(37,99,235,0.6)';
                    const focusShadow = isDark ? '0 0 0 3px rgba(245,158,11,0.12)' : '0 0 0 3px rgba(37,99,235,0.12)';
                    e.target.style.borderColor = focusColor;
                    e.target.style.boxShadow = focusShadow;
                    if (companyName.trim().length >= MIN_QUERY_LENGTH) setSuggestionsOpen(true);
                  }}
                  onBlur={(e) => { e.target.style.borderColor = theme.inputBorder; e.target.style.boxShadow = 'none'; }}
                  autoComplete="off"
                  data-testid="company-name-input"
                  style={{
                    background: theme.inputBg,
                    border: `1px solid ${theme.inputBorder}`,
                    color: theme.textPrimary,
                    outline: 'none',
                    width: '100%', height: 40, paddingLeft: 36, paddingRight: 12, borderRadius: 10, fontSize: 13, boxSizing: 'border-box', transition: 'border-color 0.2s, box-shadow 0.2s'
                  }}
                />
                {suggestionsOpen && (suggestions.length > 0 || loadingSuggestions || suggestionsError) && (
                  <div data-testid="company-suggestions" style={{ position: 'absolute', left: 0, right: 0, top: 'calc(100% + 4px)', background: isDark ? 'rgba(20,17,14,0.99)' : '#ffffff', border: `1px solid ${theme.border}`, borderRadius: 12, boxShadow: isDark ? '0 20px 60px rgba(0,0,0,0.7)' : '0 12px 32px rgba(0,0,0,0.1)', maxHeight: 220, overflowY: 'auto', zIndex: 20 }}>
                    {loadingSuggestions && <div style={{ padding: '12px 16px', color: theme.textMuted, fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}><Loader2 size={14} className="animate-spin" style={{ color: isDark ? theme.accent : '#2563eb' }} />Loading…</div>}
                    {!loadingSuggestions && suggestionsError && <div style={{ padding: '12px 16px', color: '#fbbf24', fontSize: 13 }}>{suggestionsError}</div>}
                    {!loadingSuggestions && !suggestionsError && suggestions.length === 0 && <div style={{ padding: '12px 16px', color: theme.textMuted, fontSize: 13 }}>No organizations found.</div>}
                    {!loadingSuggestions && suggestions.map(org => (
                      <button key={org.id} type="button" data-testid={`suggestion-${org.id}`}
                        onClick={() => handleSelectOrg(org)}
                        style={{ width: '100%', textAlign: 'left', padding: '10px 16px', background: 'none', border: 'none', color: theme.textPrimary, fontSize: 14, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${theme.border}` }}
                        onMouseEnter={e => e.currentTarget.style.background = isDark ? 'rgba(245,158,11,0.08)' : 'rgba(37,99,235,0.04)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'none'}
                      >
                        <span style={{ fontWeight: 500 }}>{org.name}</span>
                        <span style={{ fontSize: 12, color: theme.textMuted }}>{typeof org.alumni_count === 'number' ? `${org.alumni_count} alumni` : ''}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* CTA */}
            <button
              type="submit" disabled={searching} data-testid="search-alumni-button"
              style={{ width: '100%', height: 44, borderRadius: 12, background: isDark ? 'linear-gradient(135deg, #F59E0B, #D97706)' : 'linear-gradient(135deg, #2563eb, #1d4ed8)', color: isDark ? '#1C1917' : '#ffffff', fontWeight: 800, fontSize: 14, border: 'none', cursor: searching ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, boxShadow: isDark ? '0 4px 24px rgba(245,158,11,0.35)' : '0 4px 24px rgba(37,99,235,0.25)', opacity: searching ? 0.7 : 1, transition: 'transform 0.15s, opacity 0.15s' }}
              onMouseEnter={e => { if (!searching) e.currentTarget.style.transform = 'translateY(-1px)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; }}
            >
              {searching ? <><Loader2 size={18} className="animate-spin" /> Searching…</> : <>Track Alumni Network <ArrowRight size={18} /></>}
            </button>
          </motion.form>

          {/* Trust signals */}
          <motion.div
            variants={{ hidden: { opacity: 0 }, visible: { opacity: 1, transition: { duration: 0.6, delay: 0.3 } } }}
            style={{ display: 'flex', alignItems: 'center', gap: 56, marginTop: 16 }}
          >
            {[['650k+', 'Alumni tracked'], ['4,000+', 'Companies'], ['Free', 'to start']].map(([val, lbl]) => (
              <div key={lbl} style={{ display: 'flex', flexDirection: 'column' }}>
                <span style={{ fontSize: 18, fontWeight: 800, color: isDark ? theme.accent : '#2563eb', fontFamily: "'Inter', serif" }}>{val}</span>
                <span style={{ fontSize: 12, color: theme.textMuted, fontWeight: 500 }}>{lbl}</span>
              </div>
            ))}
          </motion.div>
        </motion.div>

        {/* Right: Network Viz */}
        <motion.div
          initial={{ opacity: 0, x: 40 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="viz-container"
          style={{ position: 'relative', zIndex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          {/* Glow behind viz */}
          <div style={{ position: 'absolute', width: 460, height: 460, borderRadius: '50%', background: isDark ? 'radial-gradient(circle, rgba(245,158,11,0.1) 0%, transparent 68%)' : 'radial-gradient(circle, rgba(37,99,235,0.08) 0%, transparent 68%)', filter: 'blur(50px)', pointerEvents: 'none' }} />
          <div style={{ width: '100%', height: '100%', maxWidth: 430, position: 'relative' }}>
            <NetworkViz isDark={isDark} />
            {/* Floating label card */}
            <motion.div
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.4, duration: 0.5 }}
              style={{ position: 'absolute', bottom: '18%', left: '-5%', background: theme.cardBg, border: isDark ? `1px solid ${theme.border}` : '1px solid #bfdbfe', borderRadius: 12, padding: '12px 16px', backdropFilter: 'blur(12px)', boxShadow: isDark ? '0 8px 32px rgba(0,0,0,0.5)' : '0 12px 32px rgba(37,99,235,0.12)' }}
            >
              <p style={{ fontSize: 12, color: isDark ? theme.accent : '#2563eb', fontWeight: 700, marginBottom: 2 }}>🔥 3 connections found</p>
              <p style={{ fontSize: 11, color: theme.textSecondary }}>at Stripe via Google alumni</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.8, duration: 0.5 }}
              style={{ position: 'absolute', top: '12%', right: '-5%', background: theme.cardBg, border: isDark ? `1px solid ${theme.border}` : '1px solid #d1fae5', borderRadius: 12, padding: '12px 16px', backdropFilter: 'blur(12px)', boxShadow: isDark ? '0 8px 32px rgba(0,0,0,0.5)' : '0 12px 32px rgba(16,185,129,0.12)' }}
            >
              <p style={{ fontSize: 12, color: '#10B981', fontWeight: 700, marginBottom: 2 }}>↑ 24 transitions</p>
              <p style={{ fontSize: 11, color: theme.textSecondary }}>to Notion this year</p>
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* ── FEATURES ── */}
      <section className="section-padding" style={{ background: theme.bg, borderTop: `1px solid ${theme.border}` }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <motion.div {...fadeUp()} style={{ textAlign: 'center', marginBottom: 64 }}>
            <p style={{ fontSize: 11, fontWeight: 700, color: isDark ? theme.accent : '#2563eb', textTransform: 'uppercase', letterSpacing: '0.15em', marginBottom: 12 }}>What you get</p>
            <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(32px, 3vw, 48px)', fontWeight: 800, color: theme.textPrimary, lineHeight: 1.15 }}>
              Everything to navigate smarter
            </h2>
          </motion.div>
          <div className="responsive-grid-3">
            {FEATURES.map((f, i) => (
              <motion.div key={f.label} {...fadeUp(i * 0.1)}
                style={{ background: theme.cardBg, border: `1px solid ${theme.border}`, borderRadius: 18, padding: '32px 28px', cursor: 'default', transition: 'border-color 0.25s, transform 0.25s', boxShadow: isDark ? 'none' : '0 10px 30px rgba(0,0,0,0.03)' }}
                whileHover={{ y: -6, transition: { duration: 0.2 } }}
                onMouseEnter={e => e.currentTarget.style.borderColor = isDark ? `rgba(${f.color === '#F59E0B' ? '245,158,11' : f.color === '#10B981' ? '16,185,129' : '59,130,246'},0.35)` : '#3b82f6'}
                onMouseLeave={e => e.currentTarget.style.borderColor = theme.border}
              >
                <div style={{ width: 48, height: 48, borderRadius: 14, background: isDark ? `${f.color}14` : 'rgba(37,99,235,0.08)', border: isDark ? `1px solid ${f.color}26` : '1px solid rgba(37,99,235,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 20 }}>
                  <f.icon size={22} color={isDark ? f.color : '#2563eb'} />
                </div>
                <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: 20, fontWeight: 700, color: theme.textPrimary, marginBottom: 10 }}>{f.label}</h3>
                <p style={{ fontSize: 14, lineHeight: 1.65, color: theme.textSecondary }}>{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section className="section-padding" style={{ background: theme.bg, borderTop: `1px solid ${theme.border}`, position: 'relative', overflow: 'hidden' }}>
        {/* warm horizon glow */}
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', background: isDark ? 'radial-gradient(ellipse 70% 40% at 50% 100%, rgba(245,158,11,0.07) 0%, transparent 70%)' : 'radial-gradient(ellipse 70% 40% at 50% 100%, rgba(37,99,235,0.04) 0%, transparent 70%)' }} />
        <div style={{ maxWidth: 1100, margin: '0 auto', position: 'relative' }}>
          <motion.div {...fadeUp()} style={{ textAlign: 'center', marginBottom: 72 }}>
            <p style={{ fontSize: 11, fontWeight: 700, color: isDark ? theme.accent : '#2563eb', textTransform: 'uppercase', letterSpacing: '0.15em', marginBottom: 12 }}>How It Works</p>
            <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(32px, 3vw, 48px)', fontWeight: 800, color: theme.textPrimary, lineHeight: 1.15 }}>Three steps to clarity.</h2>
          </motion.div>
          <div className="responsive-grid-3" style={{ position: 'relative' }}>
            <div style={{ position: 'absolute', top: 40, left: '20%', right: '20%', height: 1, background: isDark ? 'linear-gradient(90deg, transparent, rgba(245,158,11,0.25), rgba(245,158,11,0.25), transparent)' : 'linear-gradient(90deg, transparent, #e2e8f0, #e2e8f0, transparent)' }} />
            {STEPS.map((s, i) => (
              <motion.div key={s.n} {...fadeUp(i * 0.12)} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                <div style={{ position: 'relative', marginBottom: 28 }}>
                  <div style={{ width: 80, height: 80, borderRadius: 22, background: isDark ? 'rgba(245,158,11,0.08)' : 'rgba(37,99,235,0.08)', border: isDark ? '1px solid rgba(245,158,11,0.22)' : '1px solid rgba(37,99,235,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: isDark ? '0 0 40px rgba(245,158,11,0.08)' : '0 10px 30px rgba(37,99,235,0.08)' }}>
                    <s.icon size={30} color={isDark ? '#F59E0B' : '#2563eb'} />
                  </div>
                  <div style={{ position: 'absolute', top: -8, right: -8, width: 24, height: 24, borderRadius: '50%', background: isDark ? '#F59E0B' : '#2563eb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800, color: '#ffffff' }}>{i + 1}</div>
                </div>
                <h3 style={{ fontSize: 16, fontWeight: 700, color: theme.textPrimary, marginBottom: 10, lineHeight: 1.3 }}>{s.title}</h3>
                <p style={{ fontSize: 13, color: theme.textSecondary, lineHeight: 1.65, maxWidth: 210 }}>{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SOCIAL PROOF ── */}
      <section className="section-padding" style={{ background: theme.bg, borderTop: `1px solid ${theme.border}` }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <motion.div {...fadeUp()} style={{ textAlign: 'center', marginBottom: 64 }}>
            <p style={{ fontSize: 11, fontWeight: 700, color: isDark ? theme.accent : '#2563eb', textTransform: 'uppercase', letterSpacing: '0.15em', marginBottom: 12 }}>Real Results</p>
            <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(32px, 3vw, 48px)', fontWeight: 800, color: theme.textPrimary, lineHeight: 1.15 }}>Built for serious job seekers</h2>
          </motion.div>
          <div className="responsive-grid-3">
            {PROOF.map((p, i) => (
              <motion.div key={i} {...fadeUp(i * 0.1)}
                style={{ background: theme.cardBg, border: `1px solid ${theme.border}`, borderRadius: 18, padding: '28px 24px', boxShadow: isDark ? 'none' : '0 10px 30px rgba(0,0,0,0.02)' }}
              >
                <div style={{ display: 'flex', gap: 3, marginBottom: 16 }}>
                  {[0, 1, 2, 3, 4].map(s => <Star key={s} size={13} fill={isDark ? theme.accent : '#2563eb'} color={isDark ? theme.accent : '#2563eb'} />)}
                </div>
                <p style={{ fontSize: 14, lineHeight: 1.7, color: theme.textSecondary, marginBottom: 20, fontStyle: 'italic' }}>"{p.text}"</p>
                <div>
                  <p style={{ fontSize: 13, fontWeight: 700, color: theme.textPrimary }}>{p.name}</p>
                  <p style={{ fontSize: 12, color: theme.textMuted }}>{p.role}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── DASHBOARD PREVIEW ── */}
      <section className="section-padding" style={{ background: theme.bg, borderTop: `1px solid ${theme.border}` }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <motion.div {...fadeUp()} style={{ textAlign: 'center', marginBottom: 52 }}>
            <p style={{ fontSize: 11, fontWeight: 700, color: isDark ? theme.accent : '#2563eb', textTransform: 'uppercase', letterSpacing: '0.15em', marginBottom: 12 }}>Preview</p>
            <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(32px, 3vw, 48px)', fontWeight: 800, color: theme.textPrimary, lineHeight: 1.15 }}>Your network, clearly mapped</h2>
          </motion.div>
          <motion.div {...fadeUp(0.1)} style={{ borderRadius: 20, overflow: 'hidden', border: `1px solid ${theme.border}`, boxShadow: isDark ? '0 40px 120px rgba(0,0,0,0.7)' : '0 20px 60px rgba(37,99,235,0.08)' }}>
            {/* Browser bar */}
            <div style={{ padding: '12px 20px', background: isDark ? '#0d1019' : '#f1f5f9', borderBottom: `1px solid ${theme.border}`, display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 12, height: 12, borderRadius: '50%', background: 'rgba(239,68,68,0.5)' }} />
              <div style={{ width: 12, height: 12, borderRadius: '50%', background: 'rgba(245,158,11,0.5)' }} />
              <div style={{ width: 12, height: 12, borderRadius: '50%', background: 'rgba(16,185,129,0.5)' }} />
              <div style={{ flex: 1, margin: '0 16px', background: isDark ? 'rgba(255,255,255,0.05)' : '#ffffff', border: `1px solid ${theme.border}`, borderRadius: 8, padding: '4px 12px', textAlign: 'center', fontSize: 12, color: theme.textMuted }}>fairhire.app/dashboard</div>
            </div>
            {/* Content */}
            <div className="responsive-grid-2" style={{ background: isDark ? '#0d1019' : '#ffffff', padding: 24 }}>
              {/* Companies */}
              <div style={{ background: theme.cardBg, border: `1px solid ${theme.border}`, borderRadius: 14, padding: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                  <h4 style={{ fontSize: 13, fontWeight: 600, color: theme.textSecondary }}>Top Destinations</h4>
                  <span style={{ fontSize: 11, color: isDark ? theme.accent : '#2563eb', fontWeight: 600 }}>1st transition</span>
                </div>
                {[{ n: 'Stripe', s: 94, c: '#10B981' }, { n: 'Notion', s: 87, c: '#3B82F6' }, { n: 'Figma', s: 79, c: '#8B5CF6' }, { n: 'Vercel', s: 72, c: isDark ? theme.accent : '#2563eb' }].map((item, idx) => (
                  <div key={item.n} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
                    <span style={{ fontSize: 11, color: theme.textDim, width: 14 }}>{idx + 1}</span>
                    <div style={{ width: 28, height: 28, borderRadius: 8, background: `${item.c}1a`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: item.c }}>{item.n[0]}</div>
                    <span style={{ flex: 1, fontSize: 13, fontWeight: 500, color: theme.textSecondary }}>{item.n}</span>
                    <div style={{ width: 80, height: 4, background: isDark ? 'rgba(255,255,255,0.08)' : '#f1f5f9', borderRadius: 2, overflow: 'hidden' }}>
                      <motion.div style={{ height: '100%', background: item.c, borderRadius: 2 }} initial={{ width: 0 }} whileInView={{ width: `${item.s}%` }} viewport={{ once: true }} transition={{ duration: 1, delay: idx * 0.1 }} />
                    </div>
                    <span style={{ fontSize: 11, color: theme.textMuted, width: 28, textAlign: 'right' }}>{item.s}%</span>
                  </div>
                ))}
              </div>
              {/* Transitions */}
              <div style={{ background: theme.cardBg, border: `1px solid ${theme.border}`, borderRadius: 14, padding: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                  <h4 style={{ fontSize: 13, fontWeight: 600, color: theme.textSecondary }}>Transition Flow</h4>
                  <span style={{ fontSize: 11, color: isDark ? theme.accent : '#2563eb', fontWeight: 600 }}>by role</span>
                </div>
                {[{ n: 'Product', v: 34, c: isDark ? theme.accent : '#2563eb' }, { n: 'Engineering', v: 28, c: '#3B82F6' }, { n: 'Design', v: 18, c: '#8B5CF6' }, { n: 'Operations', v: 12, c: '#10B981' }, { n: 'Growth', v: 8, c: '#EC4899' }].map(item => (
                  <div key={item.n} style={{ marginBottom: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: theme.textSecondary, marginBottom: 5 }}><span>{item.n}</span><span>{item.v}%</span></div>
                    <div style={{ height: 4, background: isDark ? 'rgba(255,255,255,0.08)' : '#f1f5f9', borderRadius: 2, overflow: 'hidden' }}>
                      <motion.div style={{ height: '100%', background: item.c, borderRadius: 2 }} initial={{ width: 0 }} whileInView={{ width: `${item.v * 2.8}%` }} viewport={{ once: true }} transition={{ duration: 1 }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section className="section-padding" style={{ background: isDark ? 'linear-gradient(180deg, #1A1510 0%, #0C0A08 100%)' : 'linear-gradient(180deg, #f1f5f9 0%, #ffffff 100%)', borderTop: `1px solid ${isDark ? 'rgba(245,158,11,0.12)' : '#e2e8f0'}`, position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, background: isDark ? 'radial-gradient(ellipse 60% 60% at 50% 50%, rgba(245,158,11,0.1) 0%, transparent 70%)' : 'radial-gradient(ellipse 60% 60% at 50% 50%, rgba(37,99,235,0.05) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <motion.div {...fadeUp()} style={{ maxWidth: 700, margin: '0 auto', textAlign: 'center', position: 'relative' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 14px', borderRadius: 100, background: isDark ? 'rgba(245,158,11,0.12)' : 'rgba(37,99,235,0.08)', border: isDark ? '1px solid rgba(245,158,11,0.3)' : '1px solid rgba(37,99,235,0.2)', marginBottom: 28 }}>
            <CheckCircle2 size={13} color={isDark ? theme.accent : '#2563eb'} />
            <span style={{ fontSize: 11, fontWeight: 700, color: isDark ? theme.accent : '#2563eb', letterSpacing: '0.1em', textTransform: 'uppercase' }}>Free to start</span>
          </div>
          <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(38px, 5vw, 68px)', fontWeight: 800, color: theme.textPrimary, lineHeight: 1.08, marginBottom: 20 }}>
            Know your advantage<br />
            <span style={{ color: isDark ? theme.accent : '#2563eb', textShadow: isDark ? '0 0 80px rgba(245,158,11,0.35)' : 'none' }}>before you reach out.</span>
          </h2>
          <p style={{ fontSize: 17, color: theme.textSecondary, marginBottom: 44, lineHeight: 1.6 }}>Stop guessing. Start with network intelligence.</p>
          <button
            onClick={handleGetStarted}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 10, height: 56, padding: '0 36px', borderRadius: 16, background: isDark ? 'linear-gradient(135deg, #F59E0B, #D97706)' : 'linear-gradient(135deg, #2563eb, #1d4ed8)', color: isDark ? '#1C1917' : '#ffffff', fontWeight: 800, fontSize: 16, border: 'none', cursor: 'pointer', boxShadow: isDark ? '0 8px 40px rgba(245,158,11,0.4)' : '0 8px 40px rgba(37,99,235,0.25)', transition: 'transform 0.2s, box-shadow 0.2s' }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = isDark ? '0 12px 50px rgba(245,158,11,0.5)' : '0 12px 50px rgba(37,99,235,0.35)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = isDark ? '0 8px 40px rgba(245,158,11,0.4)' : '0 8px 40px rgba(37,99,235,0.25)'; }}
          >
            Get Started <ArrowRight size={20} />
          </button>
        </motion.div>
      </section>

      {/* ── FOOTER ── */}
      <footer style={{ padding: '22px 56px', borderTop: `1px solid ${theme.border}`, background: theme.bg }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <p style={{ fontSize: 13, color: theme.textMuted, margin: 0 }}>© {currentYear} Fairhire. All rights reserved.</p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', justifyContent: 'flex-end', marginLeft: 'auto' }}>
            <button
              type="button"
              onClick={() => navigate('/terms-conditions')}
              style={{ fontSize: 13, color: theme.textSecondary, textDecoration: 'none', fontWeight: 500, background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
              onMouseEnter={e => e.currentTarget.style.color = isDark ? theme.accent : '#2563eb'}
              onMouseLeave={e => e.currentTarget.style.color = theme.textSecondary}
            >
              T&C
            </button>
            <button
              type="button"
              onClick={() => navigate('/refund-cancellation-policy')}
              style={{ fontSize: 13, color: theme.textSecondary, textDecoration: 'none', fontWeight: 500, background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
              onMouseEnter={e => e.currentTarget.style.color = isDark ? theme.accent : '#2563eb'}
              onMouseLeave={e => e.currentTarget.style.color = theme.textSecondary}
            >
              Refund Policy
            </button>
            <a href="mailto:support@fairhire.in" style={{ fontSize: 13, color: theme.textSecondary, textDecoration: 'none', fontWeight: 500 }}
              onMouseEnter={e => e.currentTarget.style.color = isDark ? theme.accent : '#2563eb'}
              onMouseLeave={e => e.currentTarget.style.color = theme.textSecondary}
            >
              Contact Us
            </a>
          </div>
        </div>
      </footer>
      <style>{`
                @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
                .animate-spin { animation: spin 1s linear infinite; }
                
                .hero-section {
                    display: grid;
                    grid-template-columns: 1fr;
                    padding: 88px 24px 44px;
                    min-height: auto;
                    gap: 40px;
                }
                @media (min-width: 1024px) {
                    .hero-section {
                        grid-template-columns: 55% 45%;
                        padding: 120px 56px 56px;
                        min-height: 100vh;
                        gap: 0;
                    }
                }
                .responsive-grid-3 {
                    display: grid;
                    grid-template-columns: 1fr;
                    gap: 16px;
                }
                @media (min-width: 640px) {
                    .responsive-grid-3 {
                        grid-template-columns: repeat(2, 1fr);
                    }
                }
                @media (min-width: 1024px) {
                    .responsive-grid-3 {
                        grid-template-columns: repeat(3, 1fr);
                        gap: 20px;
                    }
                }

                .responsive-grid-2 {
                    display: grid;
                    grid-template-columns: 1fr;
                    gap: 16px;
                }
                @media (min-width: 768px) {
                    .responsive-grid-2 {
                        grid-template-columns: 1fr 1fr;
                        gap: 20px;
                    }
                }

                .nav-container {
                   padding: 10px 20px;
                }
                @media (min-width: 768px) {
                    .nav-container {
                        padding: 10px 36px;
                    }
                }

                .hero-text-container {
                    padding-right: 0;
                    text-align: center;
                }
                @media (min-width: 1024px) {
                    .hero-text-container {
                        padding-right: 34px;
                        text-align: left;
                    }
                }

                .viz-container {
                    height: 300px;
                }
                @media (min-width: 1024px) {
                    .viz-container {
                        height: 430px;
                    }
                }

                .section-padding {
                    padding: 44px 24px;
                }
                @media (min-width: 768px) {
                    .section-padding {
                        padding: 64px 56px;
                    }
                }
            `}</style>
    </div>
  );
};

export default LandingPage;
